import { pageurl } from '@constants/pageurl';
import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { IELECT_MARK_DATA_URL } from '../../../assets/images/ielect-mark-data';
import { type IActions } from '../../../interface/IAction';
import { type IStates } from '../../../interface/IReducer';
import { partyInitials } from '../../../utils/partyInitials';
import DataWrapper from '../../../wrapper/data-wrapper';
import { ShieldCheckGlyph } from '../agent-application/create/TrustedInviteNotice';
import Certificate from './Certificate';
import {
	DetailGrid,
	EmptyState,
	HeroBillboard,
	OffscreenHost,
	Panel,
	SealMedallion,
	StatusLabel,
	StatusPill,
	StatusRow,
	StepList,
	SuccessContent,
	SuccessHeader,
	SuccessPageWrapper,
	SupportNote,
	TrustedTag,
} from './styled';
import { formatSubmittedDate, fullName, readSuccessPayload, roleLabel, statusMeta, type SuccessPayload } from './types';
import { useCertificateDownload } from './useCertificateDownload';
import { useVolunteerStatus } from './useVolunteerStatus';

const CheckIcon = () => (
	<svg
		width='18'
		height='18'
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M20 6L9 17l-5-5'
			stroke='currentColor'
			strokeWidth='2.5'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

const DownloadIcon = () => (
	<svg
		width='18'
		height='18'
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2'
			stroke='currentColor'
			strokeWidth='2'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

const NEXT_STEPS = [
	{
		title: 'We review your application',
		body: 'Our team checks your details and identity verification. This usually takes a few days.',
	},
	{
		title: 'You get an email if selected',
		body: 'Successful applicants receive an email with deployment details and next steps.',
	},
	{
		title: 'You accept and get onboarded',
		body: 'Confirm your offer to join your polling unit team ahead of election day.',
	},
];

interface SuccessChildProps {
	states?: IStates;
}

const ApplicationSuccessChild: React.FC<SuccessChildProps> = ({ states, ...props }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { getVolunteerStatusAction } = props as unknown as IActions;

	// Prefer the freshly-submitted payload passed through navigation state; fall
	// back to sessionStorage so a refresh still renders. If neither exists the
	// page was opened cold — send the visitor home.
	const payload = useMemo<SuccessPayload | null>(() => {
		const fromState = (location.state as SuccessPayload | null) ?? null;
		if (fromState?.id) return fromState;
		return readSuccessPayload();
	}, [location.state]);

	const certificateRef = useRef<HTMLDivElement>(null);
	const { downloading, downloadCertificate } = useCertificateDownload(
		certificateRef,
		payload ? `iVolunteer-Certificate-${payload.reference}` : 'iVolunteer-Certificate'
	);
	const liveStatus = useVolunteerStatus(payload?.id, getVolunteerStatusAction);

	useEffect(() => {
		if (payload) return undefined;
		const timer = window.setTimeout(() => void navigate(pageurl.OVERVIEW, { replace: true }), 2500);
		return () => window.clearTimeout(timer);
	}, [payload, navigate]);

	if (!payload) {
		return (
			<SuccessPageWrapper>
				<EmptyState>
					<h2>Nothing to show here</h2>
					<p>Taking you back to the homepage…</p>
				</EmptyState>
			</SuccessPageWrapper>
		);
	}

	const status = liveStatus ?? statusMeta(payload.statusId);
	const firstName = payload.firstName || 'there';

	return (
		<SuccessPageWrapper>
			<SuccessHeader>
				<button
					type='button'
					className='brand'
					onClick={() => navigate(pageurl.OVERVIEW)}
					aria-label='iVolunteer home'
				>
					<img
						className='brand-mark'
						src={IELECT_MARK_DATA_URL}
						alt=''
					/>
					<span className='brand-name'>iVolunteer</span>
				</button>
				<button
					type='button'
					className='home-link'
					onClick={() => navigate(pageurl.OVERVIEW)}
				>
					Back to home
				</button>
			</SuccessHeader>

			<SuccessContent>
				<HeroBillboard>
					<SealMedallion>
						<div className='seal-disc'>{partyInitials(payload.partyName || payload.partyLongName || 'iV')}</div>
						<div className='seal-check'>
							<CheckIcon />
						</div>
					</SealMedallion>

					<span className='eyebrow'>Application received</span>
					<h1>
						Thank you, <em>{firstName}</em>.
						<br />
						You’re on the roster.
					</h1>
					<p className='subcopy'>
						Your application is in. We’ll review it and, if you’re selected, you’ll get an email with everything you need for
						election day.
					</p>

					<div className='hero-cta'>
						<button
							type='button'
							className='btn-primary'
							onClick={() => void downloadCertificate()}
							disabled={downloading}
							aria-busy={downloading}
						>
							<DownloadIcon />
							{downloading ? 'Preparing…' : 'Download certificate'}
						</button>
						<button
							type='button'
							className='btn-ghost'
							onClick={() => navigate(pageurl.OVERVIEW)}
						>
							Back to home
						</button>
					</div>
				</HeroBillboard>

				<StatusRow>
					<StatusLabel>Application status</StatusLabel>
					<StatusPill tone={status.tone}>
						<span className='dot' />
						{status.label}
					</StatusPill>
					{payload.isTrustedAgent ? (
						<TrustedTag>
							<ShieldCheckGlyph />
							Applied as a trusted agent
						</TrustedTag>
					) : null}
				</StatusRow>

				<Panel>
					<h2 className='panel-title'>What happens next</h2>
					<StepList>
						{NEXT_STEPS.map((step, index) => (
							<li key={step.title}>
								<span className='step-index'>{index + 1}</span>
								<div className='step-body'>
									<h4>{step.title}</h4>
									<p>{step.body}</p>
								</div>
							</li>
						))}
					</StepList>
				</Panel>

				<Panel>
					<h2 className='panel-title'>Your application</h2>
					<DetailGrid>
						<div className='detail'>
							<dt>Reference</dt>
							<dd className='mono'>{payload.reference}</dd>
						</div>
						<div className='detail'>
							<dt>Submitted</dt>
							<dd>{formatSubmittedDate(payload.dateCreated) || 'Just now'}</dd>
						</div>
						<div className='detail'>
							<dt>Role</dt>
							<dd>{roleLabel(payload.role)}</dd>
						</div>
						{payload.isTrustedAgent ? (
							<div className='detail'>
								<dt>Application type</dt>
								<dd>Trusted agent</dd>
							</div>
						) : null}
						{payload.electionName ? (
							<div className='detail'>
								<dt>Election</dt>
								<dd>{payload.electionName}</dd>
							</div>
						) : null}
						{payload.partyLongName || payload.partyName ? (
							<div className='detail'>
								<dt>Party</dt>
								<dd>{payload.partyLongName || payload.partyName}</dd>
							</div>
						) : null}
						{payload.pollingUnitName ? (
							<div className='detail'>
								<dt>Polling unit</dt>
								<dd>
									{payload.pollingUnitName}
									{payload.pollingUnitCode ? ` · ${payload.pollingUnitCode}` : ''}
								</dd>
							</div>
						) : null}
						{payload.locationNote ? (
							<div className='detail'>
								<dt>Location</dt>
								<dd>{payload.locationNote}</dd>
							</div>
						) : null}
					</DetailGrid>
				</Panel>

				<SupportNote>
					Not sure what comes next?{' '}
					<a
						href={pageurl.INSTRUCTIONS}
						onClick={(e) => {
							e.preventDefault();
							navigate(pageurl.INSTRUCTIONS);
						}}
					>
						See how it works
					</a>
					.
				</SupportNote>
			</SuccessContent>

			<OffscreenHost aria-hidden='true'>
				<Certificate
					ref={certificateRef}
					payload={payload}
				/>
			</OffscreenHost>
		</SuccessPageWrapper>
	);
};

const ApplicationSuccessMemo = React.memo(ApplicationSuccessChild);

const ApplicationSuccess: React.FC = () => (
	<DataWrapper nowrapper='true'>
		<ApplicationSuccessMemo />
	</DataWrapper>
);

export default ApplicationSuccess;
